import React, { useMemo } from "react";

import {
  Button,
  ButtonGroup,
  Stack,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AppsIcon from "@mui/icons-material/Apps";
import TableRowsIcon from "@mui/icons-material/TableRows";

import { UserType } from "../../types";
import { userHasPermission } from "../../types-helpers";
import { createUserDictionary } from "../../utils/create-dictionary";

import {
  useGetCurrentUser,
  useListOrganisations,
  useListUsers,
} from "../../store";

import TeamsCardsView from "./components/teams-cards-view";
import TeamsTableView from "./components/teams-table-view";
import MainLayout from "../../layouts/main-layout";
import CreateTeamModal from "./components/create-team-modal";

const TeamsPage: React.FC = () => {
  const { data: currentUser, isLoading: isCurrentUserLoading } = useGetCurrentUser();
  const { data: teams, isLoading: isTeamsLoading } = useListOrganisations();
  const { data: usersList = [], isLoading: isUsersListLoading } = useListUsers()

  const isLoading = isCurrentUserLoading || isTeamsLoading || isUsersListLoading;
  const isClientAdmin = userHasPermission(UserType.SysAdmin, currentUser);

  const userDictionary = useMemo(() => {
    return createUserDictionary(usersList);
  }, [usersList]);

  const [addTeamModalOpen, setAddTeamModalOpen] =
    React.useState<boolean>(false);

  const [searchQuery, setSearchQuery] = React.useState<string>("");
  const [viewMode, setViewMode] = React.useState<"cards" | "list">("list");

  const canEditOrgs: string[] = useMemo(() => {
    if (!teams || !currentUser) {
      return []
    }

    if (userHasPermission(UserType.SysAdmin, currentUser)) {
      return teams.map(item => item.organisationId)
    }

    return teams.filter(item => item.adminIds.includes(currentUser.userId)).map(item => item.organisationId)

  }, [currentUser, teams])

  // Filter teams based on search query and keep a stable default A-Z order.
  const filteredTeams = React.useMemo(() => {
    if (!teams) {
      return [];
    }

    // Filter based on search query
    const query = searchQuery.toLowerCase().trim();
    const filtered = query
      ? teams.filter((team) => {
        // Search by team name only
        const matchesTeam = team.name.toLowerCase().includes(query);

        // Search by user names in this team
        const matchesUser = usersList?.some(
          (user) =>
            user.organisationIds.includes(team.organisationId) &&
            (user.fullName?.toLowerCase().includes(query) ||
              user.email?.toLowerCase().includes(query))
        );

        return matchesTeam || matchesUser;
      })
      : teams;

    return [...filtered].sort((a, b) => {
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });

  }, [teams, usersList, searchQuery]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleViewChange = (newView: "cards" | "list") => {
    setViewMode(newView);
  };

  const openModal = () => {
    setAddTeamModalOpen(true);
  };

  const closeModal = () => {
    setAddTeamModalOpen(false);
  };

  return (
    <MainLayout fixedHeight>
      <Stack
        spacing={0}
        sx={{
          flex: 1,
          minHeight: 0,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "start", sm: "center" }}
          sx={{ flexShrink: 0 }}
          mb={2}
        >
          <Typography variant="h3">Teams</Typography>
          {isClientAdmin && (
            <Button
              variant="contained"
              onClick={openModal}
              startIcon={<AddIcon />}
              sx={{
                borderRadius: "1.5rem",
                px: 2,
                py: 1,
                height: "40px",
                fontWeight: 600,
              }}
            >
              New Team
            </Button>
          )}
        </Stack>

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          sx={{ flexShrink: 0 }}
        >
          <ButtonGroup
            variant="outlined"
            sx={{
              borderRadius: "0.5rem",
              "& .MuiButton-root": {
                color: "#555",
                borderColor: "#BBB",
                "&:not(:last-child)": {
                  borderRight: "1px solid #BBB",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#f5f5f5",
                  color: "#999",
                },
              },
              "& .MuiButton-root:first-of-type": {
                borderTopLeftRadius: "0.5rem",
                borderBottomLeftRadius: "0.5rem",
              },
              "& .MuiButton-root:last-of-type": {
                borderTopRightRadius: "0.5rem",
                borderBottomRightRadius: "0.5rem",
              },
            }}
          >
            <Tooltip title="List view">
              <Button
                onClick={() => handleViewChange("list")}
                aria-pressed={viewMode === "list"}
                sx={{ px: 1.5, py: 0.875, minWidth: 0 }}
              >
                <TableRowsIcon
                  sx={{
                    fontSize: 22,
                    color: viewMode === "list" ? "primary.main" : "#555",
                  }}
                />
              </Button>
            </Tooltip>
            <Tooltip title="Card view">
              <Button
                onClick={() => handleViewChange("cards")}
                aria-pressed={viewMode === "cards"}
                sx={{ px: 1.5, py: 0.875, minWidth: 0 }}
              >
                <AppsIcon
                  sx={{
                    fontSize: 22,
                    color: viewMode === "cards" ? "primary.main" : "#555",
                  }}
                />
              </Button>
            </Tooltip>
          </ButtonGroup>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Search teams by name or user"
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="Clear team search"
                    onClick={() => setSearchQuery("")}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
            sx={{
              flex: 1,
              minWidth: { xs: "100%", sm: 280 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "0.5rem",
              },
            }}
          />
        </Stack>

        {isLoading ? (
          <Stack
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 200,
            }}
          >
            <CircularProgress />
          </Stack>
        ) : filteredTeams.length === 0 ? (
          <Stack
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: 200,
              flexDirection: "column",
            }}
          >
            <Typography variant="h6" color="text.secondary">
              No teams found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search or add a new team
            </Typography>
          </Stack>
        ) : (
          <Stack direction={"column"} sx={{ flex: 1, minHeight: 0 }}>
            {viewMode === "cards" ? (
              <TeamsCardsView
                filteredTeams={filteredTeams}
                unfilteredTeamsCount={teams?.length || 0}
                users={userDictionary}
                canEditOrgs={canEditOrgs}
              />
            ) : (
              <TeamsTableView
                filteredTeams={filteredTeams}
                unfilteredTeamsCount={teams?.length || 0}
                users={userDictionary}
                canEditOrgs={canEditOrgs}
              />
            )}
          </Stack>
        )}

        <CreateTeamModal
          open={addTeamModalOpen}
          onClose={closeModal}
        />
      </Stack>
    </MainLayout>
  );
};

export default TeamsPage;
